import { defaultNamespace, LOCALES, LocaleEnum, namespaces } from "@repo/i18n";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import { validationMessage } from "./validation";

/**
 * Catalogs come straight out of `packages/i18n`: one lazy, content-hashed Vite
 * chunk per locale × namespace, so a deploy can never serve a stale JSON the
 * way a `public/locales` fetch could. The glob is relative to this file.
 */
const catalogs = import.meta.glob<{ default: Record<string, unknown> }>(
  "../../../../../packages/i18n/locales/*/*.json",
);

const catalogPath = (lng: string, ns: string): string =>
  `../../../../../packages/i18n/locales/${lng}/${ns}.json`;

/**
 * Every namespace the SPA renders (`emails` is API-only). All of them load
 * before the first render (see `i18nReady`), so no screen ever shows raw keys
 * while its module's chunk is in flight; together they are a few tens of KB.
 */
const webNamespaces = namespaces.filter((ns) => ns !== "emails");

/** Resolves once the active language's catalogs are in; `main.tsx` renders after it. */
export const i18nReady = i18n
  .use(
    resourcesToBackend((lng: string, ns: string) => {
      const load = catalogs[catalogPath(lng, ns)];
      return load
        ? load()
        : Promise.reject(new Error(`No catalog for ${lng}/${ns}`));
    }),
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: LocaleEnum.EN,
    supportedLngs: [...LOCALES],
    // `cs-CZ` from the browser resolves to the `cs` catalog.
    load: "languageOnly",
    ns: webNamespaces,
    defaultNS: defaultNamespace,
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

/**
 * Every Zod parse in the tab, including the shared `@repo/schema` ones, words
 * its issues through the `validation` catalog in the language active at parse
 * time. Global by design: one tab, one user, one language. Never do this in
 * the API, where requests do not share a language.
 */
z.config({ customError: (issue) => validationMessage(issue) });

export default i18n;
